import type { AgentReport } from '@shouyi/shared';
import type { AnalysisReuse } from './store.js';

export const SNAPSHOT_PUBLISH_SCHEDULE_LABEL = '交易日 09:15';

const SHANGHAI_DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

export function displayDate(value: string | null | undefined): string {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 8);
  return digits.length === 8 ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}` : '--';
}

export function displayDateTime(value: string | null | undefined): string {
  const date = new Date(String(value ?? ''));
  if (!Number.isFinite(date.getTime())) return '--';
  const parts = SHANGHAI_DATE_TIME.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}:${part('second')}`;
}

export function displayNumber(value: number | null | undefined, digits = 1): string {
  return value === null || value === undefined || !Number.isFinite(value) ? '--' : value.toFixed(digits);
}

export function displayPct(value: number | null | undefined, digits = 1): string {
  return value === null || value === undefined || !Number.isFinite(value) ? '--' : `${value.toFixed(digits)}%`;
}

export function displaySignedPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--';
  return `${value > 0 ? '+' : ''}${value.toFixed(digits)}%`;
}

export function statusLabel(value: string): string {
  return ({ queued: '排队中', leased: '已领取', running: '分析中', completed: '已完成', failed: '失败', cancelled: '已取消' } as Record<string, string>)[value] ?? value;
}

export function conclusionLabel(value: string): string {
  return ({ favorable: '积极观察', neutral: '中性观察', avoid: '风险回避' } as Record<string, string>)[value] ?? value;
}

export function stageLabel(value: string): string {
  return ({ queued: '等待分析', evidence: '核验证据', collecting_evidence: '收集证据', analyzing: '综合分析', analysis: '综合分析', completed: '分析完成', failed: '分析失败', published: '报告已发布', reporting: '生成报告', claimed: '已领取任务', running: '处理中' } as Record<string, string>)[value] ?? '证据处理';
}
export function evidenceStatusLabel(value: string): string {
  return ({ complete: '证据完整', partial: '证据不完整', failed: '证据校验未通过', insufficient_evidence: '证据不足', unavailable: '来源不可用' } as Record<string, string>)[value] ?? '待核验';
}

export function analysisModeLabel(value: string): string {
  return ({ historical_strict: '严格历史研究', live_research: '候选快照研究', ad_hoc_research: '按需个股研究' } as Record<string, string>)[value] ?? '研究模式未标注';
}

export function analysisReuseLabel(value: AnalysisReuse): string {
  const action = { new: '已建立新任务', in_progress: '已复用进行中的任务，无需重复分析', completed: '已复用有效期内的报告' }[value.kind];
  return `${action}；${value.quotaCharged ? '本次计入分析额度' : '本次未扣分析额度'}。`;
}

export function reportEngineLabel(report: AgentReport): string {
  if (report.model.engine.includes('deterministic') || report.usage.usageStatus === 'not_applicable') return '规则研究报告';
  if (report.usage.usageStatus === 'provider_reported' || (report.usage.llmCalls ?? 0) > 0) return '模型研究报告';
  return '研究报告（模式未标注）';
}

export function reportUsageLabel(report: AgentReport): string {
  const usage = report.usage;
  if (usage.usageStatus === 'not_applicable') return '未调用语言模型 · Token 用量不适用';
  if (usage.usageStatus !== 'provider_reported') return `Token 用量未知（无供应商用量记录）${usage.llmCalls === undefined ? '' : ` · 模型调用 ${usage.llmCalls} 次`}`;
  const token = (value: number | null) => value === null ? '未知' : String(value);
  return `输入 ${token(usage.promptTokens)} / 输出 ${token(usage.completionTokens)} tokens · 模型调用 ${usage.llmCalls ?? '未知'} 次`;
}
